/**
 * Error Boundary Component
 * Catches React errors in child components and displays fallback UI
 *
 * Note: Error boundaries don't catch errors in:
 * - Event handlers (use try-catch instead)
 * - Asynchronous code (setTimeout, promises, fetch)
 * - Server-side rendering
 * - Errors thrown in the error boundary itself
 */

import React from 'react'
import type { ErrorBoundaryProps, ErrorBoundaryState } from '../types/errors'
import { ErrorFallback } from './ErrorFallback'
import { logError } from '../utils/errorLogger'

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  /**
   * Static method called when an error is thrown in a child component
   * Updates state to trigger fallback UI rendering
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    }
  }

  /**
   * Lifecycle method called after an error is caught
   * Used for error logging and reporting
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to console (and future error tracking service)
    logError(error, errorInfo)

    // Update state with error info
    this.setState({
      errorInfo,
    })

    // Call optional error callback prop
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  /**
   * Resets error boundary state to allow re-rendering of children
   * Called when user clicks "Try Again" button
   */
  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  /**
   * Reloads the entire page
   * Called when user clicks "Reload Page" button
   */
  reloadPage = (): void => {
    window.location.reload()
  }

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error && this.state.errorInfo) {
      // Render error fallback UI
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.resetErrorBoundary}
          onReload={this.reloadPage}
        />
      )
    }

    // Render children normally when no error
    return this.props.children
  }
}
